import { create } from 'zustand';

interface PasswordStoreState {
  actualPassword: string;
  maskedValue: string;
  showPassword: boolean;
  setActualPassword: (password: string) => void;
  setMaskedValue: (masked: string) => void;
  toggleShowPassword: (showPassword: boolean) => void;
  handlePasswordChange: (newValue: string, cursorPosition?: number) => string;
  clearPassword: () => void;
  getDisplayValue: () => string;
  resetData: () => void;
}

const usePasswordStore = create<PasswordStoreState>((set, get) => ({
  actualPassword: '',
  maskedValue: '',
  showPassword: false,

  resetData: () => set({actualPassword: '', maskedValue: '', showPassword: false}),
  
  setActualPassword: (password) => set({ actualPassword: password }),
  
  setMaskedValue: (masked) => set({ maskedValue: masked }),
  
  toggleShowPassword: (showPassword) => set({ 
    showPassword: showPassword
  }),
  
  handlePasswordChange: (newValue, cursorPosition = 0) => {
    const { actualPassword, maskedValue, showPassword } = get();
    
    // If showing password, treat input as actual password
    if (showPassword) {
      const newMaskedValue = '*'.repeat(newValue.length);
      set({ 
        actualPassword: newValue,
        maskedValue: newMaskedValue 
      });
      return newValue;
    }
    
    // When password is hidden, we need to detect what changed
    const currentDisplayLength = maskedValue.length;
    const newDisplayLength = newValue.length;
    
    if (newDisplayLength > currentDisplayLength) {
      // Characters were added
      // Find what was actually typed (non-asterisk characters)
      let addedChars = '';
      for (let i = 0; i < newValue.length; i++) {
        if (i >= maskedValue.length || newValue[i] !== '*') {
          addedChars += newValue[i];
        }
      }
      
      // If no real characters found, assume they're at the end
      if (addedChars === '' || addedChars.includes('*')) {
        addedChars = newValue.slice(currentDisplayLength);
      }
      
      const newActualPassword = actualPassword + addedChars;
      const newMaskedValue = '*'.repeat(newActualPassword.length);
      
      set({ 
        actualPassword: newActualPassword,
        maskedValue: newMaskedValue 
      });
      return newActualPassword;
      
    } else if (newDisplayLength < currentDisplayLength) {
      // Characters were deleted
      const deletedCount = currentDisplayLength - newDisplayLength;
      const newActualPassword = actualPassword.slice(0, -deletedCount);
      const newMaskedValue = '*'.repeat(newActualPassword.length);
      
      set({ 
        actualPassword: newActualPassword,
        maskedValue: newMaskedValue 
      });
      return newActualPassword;
      
    } else {
      // Same length - could be replacement or cursor movement
      // For simplicity, if someone selects and types, treat as new input
      if (newValue.match(/[^*]/)) {
        // Contains non-asterisk characters, treat as new input
        const newActualPassword = newValue.replace(/\*/g, '');
        const newMaskedValue = '*'.repeat(newValue.length);
        
        set({ 
          actualPassword: newActualPassword,
          maskedValue: newMaskedValue 
        });
        return newActualPassword;
      }
      
      // All asterisks, no change
      return actualPassword;
    }
  },
  
  clearPassword: () => set({ 
    actualPassword: '',
    maskedValue: '',
  }),
  
  getDisplayValue: () => {
    const { actualPassword, maskedValue, showPassword } = get();
    return showPassword ? actualPassword : maskedValue;
  }
}));

export default usePasswordStore;